import React, { Fragment } from 'react';
import { useIntl } from 'react-intl';

// Hooks
import { useLocation } from '@reach/router';
import { itemIsActive } from '../../lib/url';
import { useNavContext } from '../../util/context/NavContext';
import usePathprefix from '../../util/hooks/usePathprefix';

// Components
import { Link } from 'gatsby';
import { SideNavLink, SideNavMenu, SideNavMenuItem } from '@carbon/react';

// Styles & types
import type { CleanLeaf, CleanSubSection } from 'src/util/NavItems';
import * as css from './LeftNav.module.scss';
import cn from 'clsx';

interface getLeafMenuItemData {
    key: string | number;
    item: CleanLeaf;
    pathname: string;
    handleClick(): void;
}
function getLeafMenuItem({ key, item, pathname, handleClick }: getLeafMenuItemData): {
    node: ReactElement;
    isActive: boolean;
} {
    const hasActiveTab: boolean = itemIsActive(item.path, pathname, 3);
    const node = (
        <SideNavMenuItem
            key={key}
            to={item.path}
            as={Link}
            children={item.title}
            isActive={hasActiveTab}
            onClick={handleClick}
        />
    );

    return { node, isActive: hasActiveTab };
}

export interface LeftNavItemProps {
    items: Array<CleanLeaf | CleanSubSection>;
    category: string;
    hasDivider?: boolean;
}
export default function LeftNavItem({ items, category, hasDivider }: LeftNavItemProps) {
    const location = useLocation();
    const pathPrefix = usePathprefix();
    const intl = useIntl();
    const locale = intl.locale || 'en';

    const { toggleNavState } = useNavContext();
    const handleClick = () => toggleNavState('leftNavIsOpen', 'close');

    const pathname = location.pathname.replace(pathPrefix, '');
    const isRootMenuActive: boolean = items.some(x => {
        if ('path' in x) return itemIsActive(x.path, pathname, 2);
        return x.pages.some(y => itemIsActive(y.path, pathname, 2));
    });
    const $divider: null | ReactElement = hasDivider ? <hr className={css.divider} /> : null;

    // Special case for single item:
    //   Do a leaf item and nothing else is nested
    const firstItem = items[0];
    if (items.length === 1 && 'path' in firstItem) {
        const to = firstItem.path;
        const isActive: boolean = itemIsActive(to, pathname, 2);

        return (
            <Fragment>
                <SideNavLink
                    to={to}
                    as={Link}
                    children={category}
                    isActive={isActive}
                    onClick={handleClick}
                    className={cn(isActive && css.currentItem)}
                />

                {$divider}
            </Fragment>
        );
    }

    const children: ReactElement[] = [];
    items.forEach((item, index) => {
        /**
         * Sub-category needs to get it's own nested "SideNavMenu".
         * Each of these sub-categories can have multiple leaf items.
         */
        if ('pages' in item && 'label' in item) {
            const title: string = item.label[locale] || item.label.en;
            let hasActiveChild: boolean = false;

            // Transform children to menu items and check
            // if any of them is active in a single loop
            const subMenuItems: ReactElement[] = [];
            item.pages.forEach((leaf, i) => {
                const { isActive, node } = getLeafMenuItem({ key: i, item: leaf, pathname, handleClick });
                if (isActive) hasActiveChild = true;
                subMenuItems.push(node);
            });

            children.push(
                <SideNavMenu
                    key={index}
                    title={title}
                    isActive={hasActiveChild}
                    defaultExpanded={hasActiveChild}
                    className={css.subCategory}
                    children={subMenuItems}
                />,
            );
        }

        // Leaf node
        else {
            const { node } = getLeafMenuItem({ key: index, item, pathname, handleClick });
            children.push(node);
        }
    });

    return (
        <Fragment>
            <SideNavMenu
                title={category}
                isActive={isRootMenuActive}
                defaultExpanded={isRootMenuActive}
                children={children}
                className={css.sideNavMenu}
            />
            {$divider}
        </Fragment>
    );
}
